import React, { useState, useEffect } from "react";
import { GetLeadDynamicData } from "../Services/APIService";

interface IntrestedProject {
  projectName: string;
  projectId: number | null;
  createdOn: string;
}

interface InterestProjectProps {
  leadId: number;
}

const InterestProject: React.FC<InterestProjectProps> = ({ leadId }) => {
  const [activeTab, setActiveTab] = useState<string>("projects");
  const [projects, setProjects] = useState<IntrestedProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCardVisible, setIsCardVisible] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await GetLeadDynamicData(leadId);
      setProjects(data.intrestedProjects || []);
    } catch (error) {
      console.error("Error fetching interested projects:", error);
      setError("Failed to load interested projects. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [leadId]);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/(\d+)\/(\d+)\/(\d+), (\d+:\d+ [AP]M)/, "$1-$2-$3, $4");
  };

  const toggleCardVisibility = () => {
    setIsCardVisible(!isCardVisible);
  };

  const renderCardBody = () => {
    if (isLoading) {
      return (
        <div className="card-body text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading projects...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="card-body text-center">
          <div className="mb-3">
            <i
              className="bi bi-exclamation-triangle"
              style={{ fontSize: "3rem", color: "#dc3545" }}
            ></i>
          </div>
          <h5 className="text-danger">{error}</h5>
        </div>
      );
    }

    return (
      <div
        className="tab-content-intrestedproject card-body p-0"
        id="myTabContent"
      >
        <div
          className={`tab-pane fade ${
            activeTab === "projects" ? "show active" : ""
          }`}
          id="tab-projects-pane"
          role="tabpanel"
          aria-labelledby="tab-projects"
          tabIndex={0}
        >
          {projects.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <i
                  className="bi bi-inbox"
                  style={{ fontSize: "3rem", color: "#6c757d" }}
                ></i>
              </div>
              <h5 className="text-muted">No records to show</h5>
              <p className="text-muted">No interested projects found.</p>
            </div>
          ) : (
            <div className="cardtask position-relative">
              {projects.map((project, index) => (
                <div
                  key={project.projectId || index}
                  className="d-flex position-relative task-item"
                >
                  {project.projectName && project.projectName.trim() !== "" && (
                    <div className="flex-grow-1">
                      <div className="card border mb-0 logData-card">
                        <div className="card-body p-3 position-relative">
                          <div className="d-flex align-items-start">
                            <div className="flex-grow-1">
                              <div className="mb-2">
                                <span className="task-notes">
                                  Project Name: {project.projectName || "N/A"}
                                </span>
                              </div>
                              <div className="text-muted task-details">
                                <span>
                                  Created On:{" "}
                                  {formatDateTime(project.createdOn ?? "")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">Intrested Project</h5>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={toggleCardVisibility}
          aria-expanded={isCardVisible}
          aria-controls="card-body-content"
        >
          <i className={`bi ${isCardVisible ? "bi-dash" : "bi-plus"}`}></i>
        </button>
      </div>
      {isCardVisible && <div id="card-body-content">{renderCardBody()}</div>}
    </div>
  );
};

export default InterestProject;
